import { createRoute, redirect, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { PageInitialDataError, unwrapPageInitialData } from "~/app/api/pageData";
import { orderDetailPageQueryOptions, type OrderDetailPageSearch } from "~/app/api/pageData/orderDetailPageQuery";
import type {
  OrderDetailFeedbackOptionData,
  OrderDetailFeedbackOptionParentData,
  OrderDetailPageInitialData,
} from "~/app/api/pageData/orderDetailPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createOrderDetailsPage, type OrderDetailsPageProps } from "~/features/orders/orderDetails/OrderDetailsPage";
import type {
  FeedbackOption,
  FeedbackOptionCategory,
  UserData,
} from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/OrderListFeedbackContext";

const ORDER_DETAIL_PATH = "/orders/$id";

/**
 * Reproduces the retired storefront page adapter's feedback-options transform verbatim
 * (near-identical to {@link orderHistoryRoute}'s adapter). Quirk
 * preserved: the parent {@code value} is the loop {@code index} as a string
 * ({@code String(index)}); the backend {@code FeedbackOptionParentData.value} is
 * discarded. Children keep their backend {@code value} unchanged.
 */
function toFeedbackChildren(options: OrderDetailFeedbackOptionData[]): FeedbackOption[] {
  return options.map((option) => ({ label: option.label, value: option.value }));
}

function toFeedbackOptionCategories(options: OrderDetailFeedbackOptionParentData[]): FeedbackOptionCategory[] {
  return options.map((option, index) => ({
    label: option.label,
    value: String(index),
    title: option.title,
    children: toFeedbackChildren(option.children),
  }));
}

function toUserData(userData: Record<string, string>): UserData {
  return {
    uid: userData.uid ?? "",
    firstName: userData.firstName ?? "",
    lastName: userData.lastName ?? "",
    email: userData.email ?? "",
    phone: userData.phone ?? "",
  };
}

function toOrderDetailProps(data: OrderDetailPageInitialData): OrderDetailsPageProps {
  return {
    userData: toUserData(data.userData),
    orderFeedbackOptions: toFeedbackOptionCategories(data.feedbackOptions),
  };
}

function OrderDetailRoute() {
  const result = useLoaderData({
    from: ORDER_DETAIL_PATH,
  }) as Awaited<ReturnType<typeof loadOrderDetailPageData>>;
  const { id } = orderDetailRoute.useParams();
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createOrderDetailsPage(toOrderDetailProps(result.data), id)}
    </StorefrontShellFrame>
  );
}

export function parseOrderDetailSearch(search: Record<string, unknown>): OrderDetailPageSearch {
  return {
    account: typeof search.account === "string" ? search.account : undefined,
    salesOrg: typeof search.salesOrg === "string" ? search.salesOrg : undefined,
  };
}

export async function loadOrderDetailPageData(
  context: { queryClient: QueryClient },
  id: string,
  referrer = document.referrer,
  search: OrderDetailPageSearch = {},
) {
  const result = await context.queryClient.ensureQueryData(orderDetailPageQueryOptions({ ...search, id }));
  const pageData = unwrapPageInitialData(result);

  if (!pageData.data) {
    throw new PageInitialDataError({
      code: "INVALID_ORDER_DETAIL_PAGE_INITIAL_DATA",
      message: "OK order detail page initial data must include data.",
    });
  }

  if (pageData.data.accountSwitchNeeded && !(pageData.data.accountSwitchSucceeded && referrer !== "")) {
    throw redirect({ href: "/orders" });
  }

  return {
    data: pageData.data,
    metadata: pageData.metadata,
  };
}

export const orderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ORDER_DETAIL_PATH,
  validateSearch: parseOrderDetailSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ context, params, deps }) => loadOrderDetailPageData(context, params.id, document.referrer, deps),
  component: OrderDetailRoute,
});
